import  { useState, useEffect, useRef, useMemo, CSSProperties } from 'react';
import { CheckCircle, AlertCircle, ChevronUp, ChevronDown, LoaderCircle } from 'lucide-react';
import { useAuth, LocalSessionState, getSessionStateString, ProgressMessage } from './AuthContext'; // Adjust the import path as needed

// LoadingIcon component with configurable color
const LoadingIcon = ({ color = 'rgba(33, 150, 243, 0.9)' }) => {
  return (
    <div className="inline-block">
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <span style={{ display: 'inline-block' }}>
        <LoaderCircle color={color} style={{ animation: 'spin 2s linear infinite' }} />
      </span>
    </div>
  );
};

// Icon colors matching the glassmorphism style
const getIconColor = (status: ProgressMessage['status']) => {
  switch (status) {
    case 'info':
      return 'rgba(33, 150, 243, 0.9)'; // Blue
    case 'success':
      return 'rgba(18, 183, 106, 0.9)'; // Green
    case 'error':
      return 'rgba(240, 68, 56, 0.9)'; // Red
    default:
      return 'rgba(219, 219, 225, 0.9)'; // Gray
  }
};

const NotificationIcon = ({ status }: { status: ProgressMessage['status'] }) => {
  const iconColor = getIconColor(status);

  switch (status) {
    case 'info':
      return <LoadingIcon color={iconColor} />;
    case 'success':
      return <CheckCircle color={iconColor} />;
    case 'error':
      return <AlertCircle color={iconColor} />;
    default:
      return null;
  }
};

const CustomNotificationCenter = () => {
  const { progressMessages, sessionState } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState<ProgressMessage[]>([]);
  const [isVisible, setIsVisible] = useState(true);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use useMemo to prevent unnecessary recalculations
  const { latestMessage, allMessages } = useMemo(() => {
    const latest = progressMessages.length > 0 
      ? progressMessages[progressMessages.length - 1] 
      : null;
    
    // Get all messages in reverse chronological order (newest first)
    const all = [...progressMessages].reverse();
      
    return { latestMessage: latest, allMessages: all };
  }, [progressMessages]);
  
  // Handle auto-hiding when status is READY
  useEffect(() => {
    // Clear any existing timeout to prevent multiple timers
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    // When session state changes to READY, set a timeout to hide
    if (sessionState === LocalSessionState.READY) {
      setIsVisible(true); // Make sure it's visible during the delay
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 3000); // Hide after 3 seconds
    } else {
      setIsVisible(true); // Always visible for other states
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [sessionState]);

  // Handle expanding/collapsing the previous messages
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Style for the main notification container
  // Using glassmorphism style matching StyledSnackbarProvider
  const getContainerStyle = (status: ProgressMessage['status']) => {
    const baseStyle: CSSProperties = {
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 5000,
      borderRadius: '8px',
      boxShadow: 'none',
      backdropFilter: 'blur(8px)',
      padding: '12px 16px',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '400px',
      width: '100%',
      transition: 'all 0.3s cubic-bezier(0.0, 0.0, 0.2, 1)',
      fontSize: '14px',
      fontWeight: 400,
    };

    // Status-specific styles with transparent tint backgrounds
    // RGB values match StyledSnackbarProvider for consistency
    switch (status) {
      case 'info':
        // Blue tint (33, 150, 243)
        return {
          ...baseStyle,
          backgroundColor: 'rgba(33, 150, 243, 0.15)',
          color: 'rgba(33, 150, 243, 0.95)',
          border: '0.5px solid rgba(33, 150, 243, 0.3)',
        };
      case 'success':
        // Green tint (18, 183, 106)
        return {
          ...baseStyle,
          backgroundColor: 'rgba(18, 183, 106, 0.15)',
          color: 'rgba(18, 183, 106, 0.95)',
          border: '0.5px solid rgba(18, 183, 106, 0.3)',
        };
      case 'error':
        // Red tint (240, 68, 56)
        return {
          ...baseStyle,
          backgroundColor: 'rgba(240, 68, 56, 0.15)',
          color: 'rgba(240, 68, 56, 0.95)',
          border: '0.5px solid rgba(240, 68, 56, 0.3)',
        };
      default:
        // Gray tint (219, 219, 225)
        return {
          ...baseStyle,
          backgroundColor: 'rgba(219, 219, 225, 0.15)',
          color: 'rgba(219, 219, 225, 0.95)',
          border: '0.5px solid rgba(219, 219, 225, 0.3)',
        };
    }
  };

  // Animation for message entry - using latestMessage.id to prevent unnecessary effects
  useEffect(() => {
    // If we have a new latest message, animate it
    if (lastMessageRef.current) {
      lastMessageRef.current.style.opacity = '0';
      const timer = setTimeout(() => {
        if (lastMessageRef.current) {
          lastMessageRef.current.style.opacity = '1';
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [latestMessage?.id]); // Only depend on the message ID

  // Manage visible messages for the history section
  useEffect(() => {
    if (isExpanded) {
      // Show messages one by one with a small delay
      const timer = setTimeout(() => {
        setVisibleMessages([...allMessages]); // All messages are already in reverse order
      }, 400);
      
      return () => clearTimeout(timer);
    } else {
      setVisibleMessages([]);
    }
  }, [isExpanded, allMessages.length]); // Only depend on the length

  if (!latestMessage || !isVisible) {
    return null;
  }

  return (
    <div 
      style={{
        ...getContainerStyle(latestMessage.status),
        animation: sessionState === LocalSessionState.READY ? 'fadeContainer 3s forwards' : 'none'
      }}
    >
      <style>
        {`
          @keyframes fadeContainer {
            0%, 80% { opacity: 1; transform: translateX(-50%); }
            100% { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          }
        `}
      </style>
      {/* Latest message */}
      <div 
        ref={lastMessageRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          transition: 'opacity 0.3s cubic-bezier(0.0, 0.0, 0.2, 1)',
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <NotificationIcon status={latestMessage.status} />
        </div>
        <div style={{ flexGrow: 1 }}>
          {latestMessage.message.toLowerCase()}
        </div>
        {allMessages.length > 1 && (
          <button
            onClick={toggleExpand}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
              borderRadius: '50%',
              color: 'inherit',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        )}
      </div>

      {/* Message history in reverse chronological order */}
      {isExpanded && (
        <div
          style={{
            marginTop: '10px',
            overflow: 'hidden',
            transition: 'max-height 0.5s cubic-bezier(0.0, 0.0, 0.2, 1)',
            maxHeight: isExpanded ? '200px' : '0',
            opacity: isExpanded ? 1 : 0,
          }}
        >
          <div
            style={{
              maxHeight: '150px',
              overflowY: 'auto',
              borderTop: '1px solid rgba(128, 128, 128, 0.2)',
              paddingTop: '10px',
            }}
          >
            {/* Skip the first item since it's already shown as the latest message */}
            {visibleMessages.slice(1).map((message, index) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '8px',
                  opacity: 0,
                  animation: `fadeIn 0.3s cubic-bezier(0.0, 0.0, 0.2, 1) forwards ${index * 0.1}s`,
                }}
              >
                <style>
                  {`
                    @keyframes fadeIn {
                      from { opacity: 0; transform: translateY(10px); }
                      to { opacity: 1; transform: translateY(0); }
                    }
                  `}
                </style>
                <div style={{ flexShrink: 0 }}>
                  <NotificationIcon status={message.status} />
                </div>
                <div style={{ fontSize: '0.9em' }}>
                  {message.message}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Show current session state for debugging */}
      {sessionState !== undefined && (
        <div style={{
          position: 'absolute',
          bottom: '-18px',
          right: '5px',
          fontSize: '10px',
          opacity: 0.5,
          color: 'inherit',
        }}>
          {getSessionStateString(sessionState)}
        </div>
      )}
    </div>
  );
};

export default CustomNotificationCenter;